import { i18nConfig } from "@/i18Config"
import { clsx, type ClassValue } from "clsx"
import * as htmlparser2 from "htmlparser2"
import { twMerge } from "tailwind-merge"

import {
  IKTImage,
  IKTRichText,
  IKTRichTextComponent,
} from "@/types/kontent.types"
import { BuiltChapters } from "@/components/ui/atoms/link/Link"
import { TIconSize } from "@/components/ui/atoms"
import { TPagination } from "@/components/ui/molecules"

import { getSyncDictionary } from "./sync-dictionary"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function isDownloadLink(url: string): boolean {
  const downloadFileTypes = [".pdf", ".zip", ".doc", ".docx", ".xls", ".xlsx"]
  return downloadFileTypes.some((ext) => url.endsWith(ext))
}

export function findDownloadSlug(slugs: string[]): string | undefined {
  return slugs.find((slug) => isDownloadLink(slug))
}

const URL_PROTOCOL_PATTERN = /^((http|https):\/\/)/

/**
 * Ensures a user-entered URL (e.g. a partner/event website from the CMS)
 * has an http(s) protocol, defaulting to https when missing.
 */
export function ensureHttpUrl(url?: string | null): string {
  const trimmed = url?.trim()
  if (!trimmed) return ""
  return URL_PROTOCOL_PATTERN.test(trimmed) ? trimmed : `https://${trimmed}`
}

export const getIconSize = (size: TIconSize): number => {
  switch (size) {
    case "xs":
      return 12
    case "sm":
      return 16
    case "md":
      return 24
    case "lg":
      return 32
    default:
      throw new Error(`Invalid icon size: ${size}`)
  }
}

export function generateRandomID(length = 10) {
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789"
  let result = ""

  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length))
  }

  return result
}

export function GetMobilePagesPagination(
  list: TPagination[],
  currentPageIndex: number,
  paginationCount: number
): TPagination[] {
  if (currentPageIndex === 0) {
    return list.slice(0, 3)
  }
  if (currentPageIndex === paginationCount - 1) {
    return list.slice(paginationCount - 3)
  }
  return list.slice(currentPageIndex - 1, currentPageIndex + 2)
}

export function capitalizeFirstLetter(string: string) {
  return string.charAt(0).toUpperCase() + string.slice(1)
}

export function formatEventDate(date: Date): string {
  return new Date(date).toLocaleDateString("pl", {
    day: "2-digit",
    month: "numeric",
    year: "2-digit",
  })
}

export function formatEventTime(date: Date): string {
  return new Date(date).toLocaleTimeString(i18nConfig.defaultLocale, {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Zurich",
  })
}

export function formatDate(date: Date): string {
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
  }
  let formattedDate: string
  formattedDate = new Intl.DateTimeFormat("fr-FR", options).format(
    new Date(date)
  )

  return formattedDate
}

type TParsedIframe = {
  url: string
  height: string | number
}

export function parseIframeUrl(iframe: string): TParsedIframe {
  let iframeDetails = {
    url: "",
    height: "",
  }
  let parser = new htmlparser2.Parser({
    onopentag(name, attributes) {
      if (name === "iframe") {
        iframeDetails.url = attributes.src
        iframeDetails.height = attributes.height
      }
    },
  })
  parser.write(iframe)
  parser.end()
  return iframeDetails
}

export function formatReadTime(readTime: number): string {
  const dictionary = getSyncDictionary()
  return `${dictionary.page.news.readingTime1}${readTime}${dictionary.page.news.readingTime2}`
}

/**
 * Resolves the href of a CMS button: a linked internal page takes priority
 * over the raw `ctaLink` (both can be set on the same content item).
 */
export function resolveButtonHref(button: {
  ctaLink?: string
  linkedPage?: { path: string }
}): string {
  return button.linkedPage
    ? `/${button.linkedPage.path}`
    : (button.ctaLink ?? "")
}

export function transformButtonStyle(
  style: string
): "primary" | "secondary" | "ghost" {
  switch (style) {
    case "Primary":
      return "primary"
    case "Secondary":
      return "secondary"
    case "Secondary Outline":
      return "secondary"
    case "Ghost":
      return "ghost"
    default:
      return "primary"
  }
}

// Source: https://gist.github.com/hagemann/382adfc57adbd5af078dc93feef01fe1
export function GetSlug(text: string): string {
  if (!text) {
    console.error("No text provided")
    return ""
  }

  const a =
    "àáâäæãåāăąçćčđďèéêëēėęěğǵḧîïíīįìıİłḿñńǹňôöòóœøōõőṕŕřßśšşșťțûüùúūǘůűųẃẍÿýžźż·/_,:;&"
  const b =
    "aaaaaaaaaacccddeeeeeeeegghiiiiiiiilmnnnnoooooooooprrsssssttuuuuuuuuuwxyyzzz----------------"
  const p = new RegExp(a.split("").join("|"), "g")

  // Remove HTML tags using regular expression
  const textWithoutTags = text.replace(/<[^>]*>/g, "")

  return textWithoutTags
    .toString()
    .toLowerCase()
    .replace(/\s+/g, "-") // Replace spaces with -
    .replace(p, (c) => b.charAt(a.indexOf(c))) // Replace special characters
    .replace(/[^\w-]+/g, "") // Remove all non-word characters except -
    .replace(/--+/g, "-") // Replace multiple - with single -
    .replace(/^-+/, "") // Trim - from start of text
    .replace(/-+$/, "") // Trim - from end of text
}
export function GetExcerpt(input: string, maxLength: number): string {
  if (input.length <= maxLength) {
    return input
  }

  const short = input.substr(0, maxLength)
  // Make sure we don't cut a word
  if (/^\S/.test(input.substring(maxLength))) {
    return `${short.replace(/\s+\S*$/, "")}...`
  }
  return `${short}...`
}

export type TParsedBody =
  | { type: "html"; content: string }
  | { type: "other"; element: IKTRichTextComponent | undefined }
  | { type: "image"; element: IKTImage | undefined }

type THtmlEntry = Extract<TParsedBody, { type: "html" }>

export function parseBody(body: IKTRichText, idPrefix: string = "richtext") {
  let parsedBody: TParsedBody[] = [{ type: "html", content: "" }]
  let isObject = false
  let isImage = false
  let linkCount = 1

  // Text/tag content is only ever appended right after pushing a fresh
  // { type: "html" } entry (see below), so the last item is always that
  // variant whenever this is called.
  const lastHtmlEntry = (): THtmlEntry =>
    parsedBody[parsedBody.length - 1] as THtmlEntry

  if (body.html === "<p><br></p>") {
    return []
  }

  let parser = new htmlparser2.Parser({
    onopentag(name, attributes) {
      if (name === "object") {
        if (attributes["data-rel"] === "link") {
          isObject = true
          parsedBody.push({
            type: "other",
            element: body.linkedItems?.items.find(
              (i) => i._system_.codename === attributes["data-codename"]
            ),
          })
        }
      } else if (name === "figure") {
        isImage = true
        parsedBody.push({
          type: "image",
          element: body.assets?.items.find(
            (i) => i.imageId === attributes["data-image-id"]
          ),
        })
      } else if (name !== "img") {
        if (name === "button") {
          lastHtmlEntry().content += `<${name} class="${attributes.class}">`
        } else if (name === "a") {
          let href = attributes.href
          if (attributes["data-item-id"]) {
            let element = body.itemHyperlinks?.items.find(
              (i) => i._system_.id === attributes["data-item-id"]
            )
            href = `/${element?._system_.path ?? ""}`
          }
          lastHtmlEntry().content += `<${name} id="${idPrefix + "-" + linkCount++}" href="${href}" class="wysiwyg-link" ${
            attributes.title ? 'title="' + attributes.title + '"' : ""
          } ${attributes.target ? 'target="' + attributes.target + '"' : ""} ${
            attributes.target === "_blank" ? 'rel="noopener noreferrer"' : ""
          }>`
        } else if (name === "table") {
          lastHtmlEntry().content +=
            "<div class='table-fade'><div class='table-container'><" +
            name +
            ">"
        } else {
          if (name === "h2") {
            parsedBody.push({
              type: "html",
              content: "",
            })
          } else if (name !== "br") {
            lastHtmlEntry().content += `<${name}>`
          }
        }
      }
    },
    ontext(text) {
      if (!isObject && !isImage) {
        lastHtmlEntry().content += text
      }
    },
    onclosetag(name) {
      if (name === "object") {
        isObject = false
        parsedBody.push({
          type: "html",
          content: "",
        })
      } else if (name === "figure") {
        isImage = false
        parsedBody.push({
          type: "html",
          content: "",
        })
      } else if (name !== "img") {
        if (name === "h2") {
          const textContent = lastHtmlEntry().content.trim()
          if (textContent) {
            const slugifiedText = GetSlug(textContent)
            lastHtmlEntry().content = `<h2 id="${slugifiedText}">${textContent}</h2>`
          }
          lastHtmlEntry().content += `</${name}>`
        } else if (name === "table") {
          lastHtmlEntry().content += "</" + name + "></div></div>"
        } else {
          lastHtmlEntry().content += "</" + name + ">"
        }
      }
    },
  })
  parser.write(body.html)
  parser.end()

  return parsedBody.filter(
    (el) => !(el.type === "html" && (el.content === "" || el.content === "\n"))
  )
}

function extractTextFromHtml(html: string): string {
  let parsedBody: string = ""
  let isText = false

  const parser = new htmlparser2.Parser({
    onopentag(name) {
      if (name !== "object" && name !== "figure" && name !== "img") {
        isText = true
      }
    },
    ontext(text) {
      if (isText) {
        parsedBody += text
      }
    },
    onclosetag(name) {
      if (name === "object" || name === "figure" || name === "img") {
        isText = true
      }
    },
  })
  parser.write(html)
  parser.end()
  return parsedBody.replace(/(\r\n|\n|\r)/gm, "")
}

export function parseBodyForSchema(body: IKTRichText) {
  return extractTextFromHtml(body.html)
}

export function parseSimpleString(body: string) {
  return extractTextFromHtml(body)
}

export function buildAnalyticsActionLinks(chapters: string[]): BuiltChapters {
  return {
    chapters: {
      click: chapters[chapters.length - 1],
      click_chapter1: chapters[chapters.length - 2] ?? "undefined",
      click_chapter2: chapters[chapters.length - 3] ?? "undefined",
      click_chapter3: chapters[chapters.length - 4] ?? "undefined",
    },
    type: "action",
  }
}

export function buildAnalyticsLinks(
  chapters: (string | undefined)[],
  // Accepts a real URL or an <a> element (HTMLAnchorElement also exposes
  // `pathname`/`toString()` per HTMLHyperlinkElementUtils), since some
  // callers pass `event.currentTarget` from an anchor's click handler.
  href: URL | HTMLAnchorElement,
  type: string | undefined,
  clickType?: string,
  clickTag?: string,
  currentUrl?: string
): BuiltChapters {
  // Extract the last chapter or default to "undefined"
  const ctaLabel: string = chapters[chapters.length - 1] ?? ""

  const pathname = href.pathname ?? ""
  // Filter out empty segments
  const slugs = pathname.split("/").filter(Boolean)

  let builtChapters: BuiltChapters
  const downloadSlug = findDownloadSlug(slugs)

  // Clean up the chapters array by removing empty strings and null values and adding "Lien ancre" as the first chapter
  let actionChapters = ["Lien ancre"].concat(
    chapters.filter(Boolean).map((chap) => chap ?? "")
  )

  // Ensure ctaLabel is the last element in the array
  actionChapters.push(ctaLabel)

  if (type === "download" && downloadSlug) {
    // DOWNLOAD
    builtChapters = {
      chapters: {
        click: ctaLabel,
        click_chapter1: downloadSlug,
        click_chapter2: "",
        click_chapter3: "",
        click_type: "download",
      },
      type: "download",
    }
  } else if (type === "action" || href.toString().startsWith("#")) {
    // ACTION
    builtChapters = buildAnalyticsActionLinks(actionChapters)
  } else if (
    type === "navigation" ||
    (currentUrl && href.toString().includes(currentUrl)) ||
    href.toString().startsWith("/") ||
    href.toString().includes("mavieensuisse.ch") ||
    href.toString().includes("canb-expat") // For preprod purpose
  ) {
    // NAVIGATION
    // Keep only the last three items of the array
    const slugsForChapters = slugs.slice(-3)

    builtChapters = {
      chapters: {
        click: ctaLabel, // Last chapter is always the CTA Label
        click_chapter1: slugsForChapters[0] ?? "",
        click_chapter2: slugsForChapters[1] ?? "",
        click_chapter3: slugsForChapters[2] ?? "",
        click_tag: clickTag ?? "",
        click_type: clickType ?? "",
      },
      type: "navigation",
    }
  } else {
    // Exit link logic
    const rebuiltChapters = chapters.slice(0, -1) // Exclude the last element (CTA label)

    builtChapters = {
      chapters: {
        click: ctaLabel,
        click_chapter1: rebuiltChapters[0] ?? "",
        click_chapter2: rebuiltChapters[1] ?? "",
        click_chapter3: rebuiltChapters[2] ?? "",
      },
      type: "exit",
    }
  }

  return builtChapters
}
